import React from "react";
import { ArrowLeft, Lock, Unlock, RotateCcw } from "lucide-react";
import { Link } from "react-router-dom";
import PilotBanner from "@/modules/racket_lab_admin/PilotBanner";
import RLNavSidebar from "@/modules/racket_lab_admin/RLNavSidebar";
import ConfirmActionDialog from "@/modules/racket_lab_admin/ConfirmActionDialog";
import useAdminApi from "@/hooks/useAdminApi";
import useAdminMutation from "@/hooks/useAdminMutation";
import { useRacketLabAdminT } from "@/i18n/racketLabAdminI18n";

/**
 * RLAdminOpsGating — Sub-sprint D-α.
 *
 * Renders the membership gating status and exposes the in-memory
 * override via three confirmation-protected actions. Override is
 * in-memory only and resets on process restart.
 */
const StatusBox = ({ label, value, testId, tone = "slate" }) => {
  const cls = {
    slate: "border-slate-200 bg-slate-50 text-slate-700",
    green: "border-emerald-200 bg-emerald-50 text-emerald-800",
    red: "border-rose-200 bg-rose-50 text-rose-800",
    amber: "border-amber-200 bg-amber-50 text-amber-800",
  }[tone];
  return (
    <div data-testid={testId} className={`flex flex-1 flex-col items-start gap-0.5 rounded-md border px-3 py-2 ${cls}`}>
      <span className="text-[10px] font-medium uppercase tracking-wide">{label}</span>
      <span className="font-mono text-sm">{String(value)}</span>
    </div>
  );
};

const RLAdminOpsGating = () => {
  const t = useRacketLabAdminT();
  const { data, loading, error, reload } = useAdminApi(
    "/admin/racket-lab/_ops/membership-gating-status",
  );
  const { run, loading: mutating } = useAdminMutation();

  const setOverride = async (value) => {
    await run("post", "/admin/racket-lab/_ops/membership-gating-override", { value });
    reload();
  };

  const effective = data?.effective_gating_enabled;
  const override = data?.override;
  const envFlag = data?.env_flag;

  return (
    <div data-testid="rl-admin-ops-gating-page" className="flex flex-col gap-4">
      <PilotBanner />
      <RLNavSidebar />

      <Link
        to="/admin/racket-lab/ops"
        data-testid="rl-ops-gating-back"
        className="inline-flex w-fit items-center gap-1 text-xs text-slate-600 hover:text-slate-900"
      >
        <ArrowLeft className="w-3.5 h-3.5" /> {t("ops.common.back_to_toolkit")}
      </Link>

      <header>
        <h1 className="text-xl font-semibold text-slate-900">{t("ops.gating.page_title")}</h1>
        <p className="text-xs text-slate-500">{t("ops.gating.subtitle")}</p>
      </header>

      {loading && <div data-testid="rl-ops-gating-loading" className="rounded-md border border-slate-200 bg-white p-4 text-sm text-slate-500">{t("ops.gating.loading")}</div>}
      {error && !loading && (
        <div data-testid="rl-ops-gating-error" className="rounded-md border border-rose-200 bg-rose-50 p-3 text-xs text-rose-800">
          {t("ops.common.error_prefix")} {error.status || ""}: {error.detail || t("ops.common.error_unknown")}
        </div>
      )}

      {!loading && !error && data && (
        <section data-testid="rl-ops-gating-card" className="flex flex-col gap-3 rounded-md border border-slate-200 bg-white p-4">
          <header className="flex items-center justify-between gap-3">
            <div>
              <h2 className="text-sm font-medium text-slate-800">{t("ops.gating.current_state")}</h2>
              <p className="text-[11px] text-slate-500">
                {t("ops.gating.current_state_hint")}
              </p>
            </div>
            {override !== null && override !== undefined && (
              <span data-testid="rl-ops-gating-override-active" className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-medium text-amber-800">
                {t("ops.gating.override_active")}
              </span>
            )}
          </header>

          <div className="flex flex-col gap-2 md:flex-row">
            <StatusBox label="env_flag" value={envFlag ?? t("ops.gating.value_unset")} testId="rl-ops-gating-env-flag" tone="slate" />
            <StatusBox label="override" value={override === null || override === undefined ? t("ops.gating.value_cleared") : override} testId="rl-ops-gating-override" tone={override === null || override === undefined ? "slate" : "amber"} />
            <StatusBox label="effective" value={effective ?? "?"} testId="rl-ops-gating-effective" tone={effective ? "green" : "red"} />
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <ConfirmActionDialog
              dataTestId="rl-ops-gating-confirm-on"
              trigger={
                <button
                  type="button"
                  data-testid="rl-ops-gating-toggle-on"
                  disabled={mutating}
                  className="inline-flex items-center gap-1 rounded-md bg-emerald-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-emerald-700 disabled:bg-emerald-300"
                >
                  <Lock className="w-3.5 h-3.5" /> {t("ops.gating.force_on.confirm")}
                </button>
              }
              title={t("ops.gating.force_on.title")}
              description={t("ops.gating.force_on.description")}
              confirmLabel={t("ops.gating.force_on.confirm")}
              onConfirm={() => setOverride(true)}
              disabled={mutating}
            />
            <ConfirmActionDialog
              dataTestId="rl-ops-gating-confirm-off"
              trigger={
                <button
                  type="button"
                  data-testid="rl-ops-gating-toggle-off"
                  disabled={mutating}
                  className="inline-flex items-center gap-1 rounded-md bg-rose-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-rose-700 disabled:bg-rose-300"
                >
                  <Unlock className="w-3.5 h-3.5" /> {t("ops.gating.force_off.confirm")}
                </button>
              }
              title={t("ops.gating.force_off.title")}
              description={t("ops.gating.force_off.description")}
              confirmLabel={t("ops.gating.force_off.confirm")}
              onConfirm={() => setOverride(false)}
              disabled={mutating}
              tone="danger"
            />
            <ConfirmActionDialog
              dataTestId="rl-ops-gating-confirm-clear"
              trigger={
                <button
                  type="button"
                  data-testid="rl-ops-gating-toggle-clear"
                  disabled={mutating}
                  className="inline-flex items-center gap-1 rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium hover:bg-slate-50 disabled:bg-slate-100"
                >
                  <RotateCcw className="w-3.5 h-3.5" /> {t("ops.gating.clear_override.confirm")}
                </button>
              }
              title={t("ops.gating.clear_override.title")}
              description={t("ops.gating.clear_override.description")}
              confirmLabel={t("ops.gating.clear_override.confirm")}
              onConfirm={() => setOverride(null)}
              disabled={mutating}
            />
          </div>
        </section>
      )}
    </div>
  );
};

export default RLAdminOpsGating;
